import { supabase } from '../lib/supabase';

export const systemService = {
    // Get the single institution details row
    getInstitutionDetails: async () => {
        try {
            const { data, error } = await supabase
                .from('institution_settings')
                .select('*')
                .limit(1)
                .maybeSingle();

            if (error) {
                console.error('Error fetching institution details:', error);
                return null;
            }
            return data;
        } catch (err) {
            console.error('Unexpected error fetching details:', err);
            return null;
        }
    },

    // Update the details
    updateInstitutionDetails: async (id, updates) => {
        try {
            // Never send generated/immutable fields back to Postgres.
            const { id: ignoredId, created_at, updated_at, ...cleanUpdates } = updates;
            cleanUpdates.updated_at = new Date().toISOString();

            let targetId = id;
            if (!targetId) {
                const { data: existing } = await supabase.from('institution_settings').select('id').limit(1).maybeSingle();
                if (existing?.id) targetId = existing.id;
            }

            if (targetId) {
                const { data, error } = await supabase
                    .from('institution_settings')
                    .update(cleanUpdates)
                    .eq('id', targetId)
                    .select();

                if (error) throw error;
                return { success: true, data: (data && data[0]) || { id: targetId, ...cleanUpdates } };
            } else {
                const { data, error } = await supabase
                    .from('institution_settings')
                    .insert(cleanUpdates)
                    .select();

                if (error) throw error;
                return { success: true, data: (data && data[0]) || cleanUpdates };
            }
        } catch (error) {
            console.error('Error updating institution details:', error);
            return { success: false, error, message: error?.message || 'Unable to save institution settings.' };
        }
    },

    // Summative examination booking is configured per academic year and
    // semester. Keep this separate from institution profile updates so a
    // change for one period never overwrites another period's window.
    getSummativeExamBookingWindow: async (academicYearId, semesterId) => {
        const { data, error } = await supabase.rpc('get_summative_exam_booking_window', {
            target_year: academicYearId || null,
            target_semester: semesterId || null,
        });
        if (error) {
            if (error.code === 'PGRST202' || /Could not find the function/i.test(error.message || '')) {
                throw new Error('Period-specific examination booking settings are not installed. Apply migration 20260101000093 and refresh.');
            }
            throw error;
        }
        return data || { configured: false, booking_enabled: true, opens_at: null, closes_at: null, status: 'unconfigured', can_book: false };
    },

    saveSummativeExamBookingWindow: async ({ academicYearId, semesterId, bookingEnabled = true, opensAt = null, closesAt = null }) => {
        const { data, error } = await supabase.rpc('upsert_summative_exam_booking_window', {
            target_year: academicYearId,
            target_semester: semesterId,
            target_enabled: bookingEnabled !== false,
            target_opens_at: opensAt || null,
            target_closes_at: closesAt || null,
        });
        if (error) {
            if (error.code === 'PGRST202' || /Could not find the function/i.test(error.message || '')) {
                throw new Error('Period-specific examination booking settings are not installed. Apply migration 20260101000093 and refresh.');
            }
            throw error;
        }
        return data;
    },

    // Re-evaluate all existing students after an administrator changes the
    // progression gate. The RPC returns processed/advanced/failed counts.
    recalculateAllStudentModuleProgression: async () => {
        try {
            const { data, error } = await supabase.rpc('recalculate_all_student_module_progression');
            if (error) throw error;
            return { success: true, data };
        } catch (error) {
            console.error('Error recalculating student module progression:', error);
            return { success: false, error, message: error?.message || 'Unable to recalculate student progression.' };
        }
    },

    // List Buckets (Validation)
    listBuckets: async () => {
        return await supabase.storage.listBuckets();
    },

    // Upload Logo
    uploadLogo: async (file) => {
        try {
            const fileExt = file.name.split('.').pop();
            const fileName = `institution-logo-${Math.random()}.${fileExt}`;
            const filePath = `${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('institution-assets')
                .upload(filePath, file);

            if (uploadError) {
                // Attempt to create bucket if it doesn't exist
                if (uploadError.message?.includes('bucket not found') || uploadError.error === 'Bucket not found') {
                    await supabase.storage.createBucket('institution-assets', { public: true });
                    const { error: retryError } = await supabase.storage
                        .from('institution-assets')
                        .upload(filePath, file);
                    if (retryError) throw retryError;
                } else {
                    throw uploadError;
                }
            }

            const { data } = supabase.storage
                .from('institution-assets')
                .getPublicUrl(filePath);

            return { success: true, url: data.publicUrl };
        } catch (error) {
            console.error('Error uploading logo:', error);
            return { success: false, error };
        }
    }
};
